import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Divider,
  Chip,
  Avatar, Alert,
  DialogContentText,
} from '@mui/material';
import {
  X,
  Calendar,
  Clock,
  FileText,
  User,
  AlertCircle,
  DollarSign,
  Briefcase
} from 'lucide-react';
import {markAsStored, getAbsenceDetails} from '../../services/absenceAPIs';
import {createLeaveById,  addLeaveJustificationByHR} from '../../services/leavesAPIs';

const AbsenceDetailsDialog = ({ setSelectToStore, absence, fetchAbsences }) => {

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [confirmPartialDay, setConfirmPartialDay] = useState(false); // New state for confirmation
  const [isConverting, setIsConverting] = useState(false); // To prevent double-click
  const [pendingConversionType, setPendingConversionType] = useState(null); // 'annual' or 'unpaid'
  const [absenceDetails, setAbsenceDetails] = useState(null);

  const absenceData = absenceDetails || absence;

  // Fetch full absence details when modal opens
  useEffect(() => {
    if (absence?.id) {
      setAbsenceDetails(null);
      setError('');
      setSuccess('');
      getAbsenceDetails(absence.id)
        .then(setAbsenceDetails)
        .catch((err) => console.error('Failed to fetch absence details:', err));
    } else {
      setAbsenceDetails(null);
    }
  }, [absence?.id]);

  const isPartialDay = absenceData?.type === 'PARTIAL_DAY';
  const isSickness = absenceData?.ty_pe === 'SICKNESS';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      SICKNESS: '#ef4444',
      LATENESS: '#f59e0b',
      EARLY_DEPARTURE: '#3b82f6',
      EMERGENCY: '#dc2626',
      APPOINTMENT: '#8b5cf6',
      PERSONAL: '#06b6d4'
    };
    return colors[type] || '#6b7280';
  };

  const getLeaveTypeColor = (leaveType) => {
    const colors = {
      FULL_DAY: '#10b981',
      HALF_DAY: '#f59e0b',
      MULTIPLE_DAYS: '#8b5cf6',
      HOURS: '#3b82f6',
      PARTIAL_DAY: '#f97316' 
    };
    return colors[leaveType] || '#6b7280';
  };

  const handleDialogClose = () => {
    setSelectToStore(null);
    setConfirmPartialDay(false);
  };

  const formatAbsenceTypeForText = (type) => {
    if (type === 'PERSONAL') {
      return 'Personal commitment';
    } else if (type === 'SICKNESS') {
      return 'Sickness';
    }
    // Convert e.g., "SICKNESS" → "sickness", "EARLY_DEPARTURE" → "early departure"
    return type
      ? type.replace(/_/g, ' ').toLowerCase()
      : 'unknown reason';
  };

  // Shared logic for conversion after confirmation
  const performConversion = async (type, e) => {
    e.preventDefault();
   
    setError('');
    setSuccess('');
    setIsConverting(true);


    const apiPayload = {
      employee: absenceData.employee,
      type: type === 'annual' ? "ANNUAL_LEAVE" : "UNPAID_LEAVE",
      justification: `${formatAbsenceTypeForText(absenceData.ty_pe)} converted to ${type === 'annual' ? 'annual leave' : 'unpaid leave'}.`,
      startDate: absenceData.date,
      endDate: absenceData.endDate || absenceData.date,
      status: 'approved',
      doubled: true,
      hours: absenceData.hours || null
      // hours: isSickness ? absenceData.hours : null
    };

    try {
      const createdLeave = await createLeaveById(apiPayload);
      console.log('Leave request instance created:', createdLeave);

      const storedAbsence = await markAsStored(absenceData.id);
      console.log('The absence has been marked as stored:', storedAbsence);

      if (type === 'unpaid' && absenceData.justificationFile) {
        try {
          const response = await fetch(absenceData.justificationFile);
          if (!response.ok) throw new Error('Failed to download justification file');
          const blob = await response.blob();
          const fileName = absenceData.justificationFile.split('/').pop() || 'justification.pdf';
          const file = new File([blob], fileName, { type: blob.type || 'application/pdf' });
          await addLeaveJustificationByHR(createdLeave, file);
        } catch (uploadErr) {
          console.error('Justification upload failed:', uploadErr);
          setError('Leave created, but justification upload failed. Please retry.');
        }
      }

      setSuccess('Leave record has been added successfully');
      fetchAbsences();
    } catch (err) {
      if (err.response?.status === 400) {
        const msg = err.response.data;
        if (msg === 'Leave request conflicts with existing request') {
          setError('There is already a leave request during this period.');
        } else if (msg === 'Insufficient leave balance') {
          setError("Leave balance isn't enough for this request.");
        } else if (msg === 'Start date cannot be in the past') {
          setError('Start date cannot be in the past');
        } else {
          setError('Failed to submit leave request. Please try again.');
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
      console.error('Submission error:', err);
    } finally {
      setIsConverting(false);
    }
  };

  const convertToAnnualVacation = (e) => {
    if (isPartialDay) {
      setPendingConversionType('annual');
      setConfirmPartialDay(true);
      return;
    }
    if (absenceData.justificationFile) {
      setError("A justified absence cannot be deducted from the annual leave balance");
      return;
    }
    performConversion('annual', e);
  };

 const convertToUnpaidVacation = (e) => {
  
  const isPersonalUnderOrEqualTwoHours = 
    absenceData.ty_pe === 'PERSONAL' && 
    absenceData.hours && 
    parseFloat(absenceData.hours) <= 2;
    // if (isPartialDay && !isSickness && !isPersonalUnderOrEqualTwoHours) {
    //   setPendingConversionType('unpaid');
    //   setConfirmPartialDay(true);
    //   return;
    // }
  
    performConversion('unpaid', e);
  };

  if (!absence) return null;

  return (
    <>
    <Dialog
      open={Boolean(absence) && !confirmPartialDay}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <div className="dialog-container">
        <DialogTitle className="dialog-header">
          <div className="header-content">
            <div className="header-left">
              <Avatar className="header-avatar">
                <Calendar size={24} />
              </Avatar>
              <div>
                <h2 className="header-title">Absence Details</h2>
                <p className="header-subtitle">Record ID: {absenceData.id?.substring(0, 8)}...</p>
              </div>
            </div>
            <IconButton onClick={handleDialogClose} className="close-button">
              <X size={20} />
            </IconButton>
          </div>
        </DialogTitle>

        <Divider />

        {error && 
          <>
            <Alert severity="error">{error}</Alert>
            <Divider />
          </>
        }

        {success && 
          <>
            <Alert severity="success">{success}</Alert>
            <Divider />
          </>
        }
               
        <DialogContent className="dialog-content">
          <div className="content-grid">
            {/* Employee Information */}
            <div className="info-card">
              <div className="info-card-header">
                <User size={18} className="info-icon" />
                <span className="info-label">Employee</span>
              </div>
              <p className="info-value">
                {absenceData.employee?.firstName} {absenceData.employee?.lastName}
              </p>
            </div>

            {/* Date Information */}
            <div className="info-card">
              <div className="info-card-header">
                <Calendar size={18} className="info-icon" />
                <span className="info-label">Start Date</span>
              </div>
              <p className="info-value">{formatDate(absenceData.date)}</p>
            </div>

            {absenceData.endDate && (
              <div className="info-card">
                <div className="info-card-header">
                  <Calendar size={18} className="info-icon" />
                  <span className="info-label">End Date</span>
                </div>
                <p className="info-value">{formatDate(absenceData.endDate)}</p>
              </div>
            )}

            {/* Leave Type */}
            <div className="info-card">
              <div className="info-card-header">
                <Briefcase size={18} className="info-icon" />
                <span className="info-label">Absence Period</span>
              </div>
              <Chip
                label={absenceData.type?.replace(/_/g, ' ')}
                style={{
                  backgroundColor: getLeaveTypeColor(absenceData.type),
                  color: 'white',
                  fontWeight: '600',
                  marginTop: '4px'
                }}
              />
            </div>

            {/* Absence Type */}
            {absenceData.ty_pe && (
              <div className="info-card">
                <div className="info-card-header">
                  <AlertCircle size={18} className="info-icon" />
                  <span className="info-label">Absence Type</span>
                </div>
                <Chip
                  label={absenceData.ty_pe.replace(/_/g, ' ')}
                  style={{
                    backgroundColor: getTypeColor(absenceData.ty_pe),
                    color: 'white',
                    fontWeight: '600',
                    marginTop: '4px'
                  }}
                />
              </div>
            )}

            {/* Hours */}
            {absenceData.hours && (
              <div className="info-card">
                <div className="info-card-header">
                  <Clock size={18} className="info-icon" />
                  <span className="info-label">Hours</span>
                </div>
                <p className="info-value">{absenceData.hours} hours</p>
              </div>
            )}

            {/* Days */}
            <div className="info-card">
              <div className="info-card-header">
                <Calendar size={18} className="info-icon" />
                <span className="info-label">Total Days</span>
              </div>
              <p className="info-value">{absenceData.days || 'N/A'} days</p>
            </div>

            {/* Recorded By */}
            {absenceData.recordedBy && (
              <div className="info-card">
                <div className="info-card-header">
                  <User size={18} className="info-icon" />
                  <span className="info-label">Recorded By</span>
                </div>
                <p className="info-value">{absenceData.recordedBy}</p>
              </div>
            )}

            {/* Created At */}
            <div className="info-card">
              <div className="info-card-header">
                <Clock size={18} className="info-icon" />
                <span className="info-label">Created At</span>
              </div>
              <p className="info-value">{formatDate(absenceData.createdAt)}</p>
            </div>
          </div>

          {/* Reason Section */}
          {absenceData.reason && (
            <div className="reason-section">
              <div className="info-card-header">
                <FileText size={18} className="info-icon" />
                <span className="info-label">Reason</span>
              </div>
              <p className="reason-text">{absenceData.reason}</p>
            </div>
          )}

          {/* Justification File */}
          {absenceData.justificationFile && (
            <div className="justification-section">
              <div className="info-card-header">
                <FileText size={18} className="info-icon" />
                <span className="info-label">Justification File</span>
              </div>
              <a href={absenceData.justificationFile} target="_blank" rel="noopener noreferrer" className="file-link">
                View Document
              </a>
            </div>
          )}
        </DialogContent>

        <Divider />

        <DialogActions className="dialog-actions">
          <Button
            variant="outlined"
            startIcon={<DollarSign size={18} />}
            className="action-button exclude-salary"
            onClick={convertToUnpaidVacation}
          >
            Exclude from Salary
          </Button>
          <Button
            variant="contained"
            startIcon={<Briefcase size={18} />}
            className="action-button exclude-vacation"
            onClick={convertToAnnualVacation}
          >
            Excluded from Annual Vacation Balance
          </Button>
        </DialogActions>
      </div>

      <style>{`
        .dialog-container {
          background: linear-gradient(to bottom, #ffffff, #f8fafc);
        }

        .dialog-header {
          padding: 24px 24px 20px 24px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-avatar {
          background: linear-gradient(135deg, #adbbda 0%, #7091e6 100%);
          width: 56px;
          height: 56px;
        }

        .header-title {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
        }

        .header-subtitle {
          margin: 4px 0 0 0;
          font-size: 14px;
          color: #64748b;
        }

        .close-button {
          color: #64748b;
          transition: all 0.2s;
        }

        .close-button:hover {
          background-color: #f1f5f9;
          color: #1e293b;
        }

        .dialog-content {
          padding: 24px !important;
        }

        .content-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .info-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
          transition: all 0.2s;
        }

        .info-card:hover {
          border-color: #cbd5e1;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          transform: translateY(-2px);
        }

        .info-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .info-icon {
          color: #667eea;
        }

        .info-label {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #64748b;
        }

        .info-value {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
        }

        .reason-section,
        .justification-section {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
          margin-top: 16px;
        }

        .reason-text {
          margin: 8px 0 0 0;
          font-size: 14px;
          line-height: 1.6;
          color: #475569;
        }

        .file-link {
          display: inline-block;
          margin-top: 8px;
          padding: 8px 16px;
          background: linear-gradient(135deg, #adbbda 0%, #7091e6 100%);
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .file-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .dialog-actions {
          padding: 16px 24px 24px 24px !important;
          gap: 12px;
          display: flex;
          justify-content: flex-end;
        }

        .action-button {
          border-radius: 8px !important;
          padding: 10px 24px !important;
          font-weight: 600 !important;
          text-transform: none !important;
          font-size: 14px !important;
          transition: all 0.2s !important;
        }

        .exclude-salary {
          border-color: #f59e0b !important;
          color: #f59e0b !important;
          background-color: #fffbeb !important;
        }

        .exclude-salary:hover {
          background-color: #fffbeb !important;
          border-color: #f59e0b !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
        }

        .exclude-vacation {
          //background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          background: linear-gradient(135deg, #adbbda 0%, #7091e6 100%);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
        }

        .exclude-vacation:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4) !important;
        }

        @media (max-width: 600px) {
          .content-grid {
            grid-template-columns: 1fr;
          }

          .dialog-actions {
            flex-direction: column;
          }

          .action-button {
            width: 100%;
          }
        }
      `}</style>
    </Dialog>
     {/* Confirmation Dialog for Partial Day */}
      <Dialog
        open={confirmPartialDay}
        onClose={() => setConfirmPartialDay(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Conversion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This absence is for a <strong>partial day</strong>. Converting it to leave will create a <strong>full-day</strong> leave record, as the system does not support partial-day leave.
            <br /><br />
            Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPartialDay(false)} disabled={isConverting}>
            Cancel
          </Button>
          <Button
            onClick={(e) => {
              performConversion(pendingConversionType, e);
              setConfirmPartialDay(false);
            }}
            variant="contained"
            color="primary"
            disabled={isConverting}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      </>
  );
};

export default AbsenceDetailsDialog;